import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import type {
  ComplianceReport,
  IntakeAnswers,
  IntakeLicense,
  LicenseOption,
} from './lib/types'
import type { RecommendedOffersFetchResult } from './plan-pricing/recommendation.types'

interface ComplianceState {
  answers: IntakeAnswers
  sectionIndex: number
  licenses: IntakeLicense[]
  licenseOptions: Record<string, LicenseOption[]>
  report: ComplianceReport | null
  recommendedOffers: RecommendedOffersFetchResult | null
}

/** Answers stored as `{ [state]: value }`, which must track `licensed_states`. */
const PER_STATE_ANSWER_KEYS = ['license_type', 'license_expires_at'] as const

const initialState: ComplianceState = {
  answers: {},
  sectionIndex: 0,
  licenses: [],
  licenseOptions: {},
  report: null,
  recommendedOffers: null,
}

const complianceSlice = createSlice({
  name: 'compliance',
  initialState,
  reducers: {
    setAnswer: (
      state,
      action: PayloadAction<{ key: string; value: unknown }>
    ) => {
      const { key, value } = action.payload
      state.answers[key] = value
      // Submit rejects a per-state answer naming a state that is no longer
      // licensed, so dropping a state has to drop what was recorded against it.
      if (key === 'licensed_states') {
        const states = Array.isArray(value) ? value.map(String) : []
        for (const perStateKey of PER_STATE_ANSWER_KEYS) {
          const current = state.answers[perStateKey]
          if (!current || typeof current !== 'object' || Array.isArray(current))
            continue
          state.answers[perStateKey] = Object.fromEntries(
            Object.entries(current as Record<string, unknown>).filter(([s]) =>
              states.includes(s)
            )
          )
        }
      }
    },
    hydrateAnswers: (state, action: PayloadAction<IntakeAnswers>) => {
      state.answers = { ...action.payload }
    },
    setSectionIndex: (state, action: PayloadAction<number>) => {
      state.sectionIndex = action.payload
    },
    setLicenses: (state, action: PayloadAction<IntakeLicense[]>) => {
      state.licenses = action.payload
    },
    setLicenseOptions: (
      state,
      action: PayloadAction<Record<string, LicenseOption[]>>
    ) => {
      state.licenseOptions = action.payload
    },
    setReport: (state, action: PayloadAction<ComplianceReport | null>) => {
      state.report = action.payload
    },
    setRecommendedOffers: (
      state,
      action: PayloadAction<RecommendedOffersFetchResult>
    ) => {
      state.recommendedOffers = action.payload
    },
    resetIntake: () => initialState,
  },
})

export const {
  setAnswer,
  hydrateAnswers,
  setSectionIndex,
  setLicenses,
  setLicenseOptions,
  setReport,
  setRecommendedOffers,
  resetIntake,
} = complianceSlice.actions

export default complianceSlice.reducer
